import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Host,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  signal,
  SkipSelf,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormArray } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '@delon/abc/loading';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { ACLService } from '@delon/acl';
import { CopyTransactionService } from '@shared-feature/components/actions/doc-edit-actions/copy-transaction/copy-transaction.service';
import { STOCK_RELATED_TRANSACTIONS } from '@shared-feature/components/actions/doc-edit-actions/transaction-type-mapper';
import { GlobalCreditLimitModalComponent } from '@shared-feature/components/modal/global-credit-limit-modal/global-credit-limit-modal.component';
import { PeppolConfirmationModalComponent } from '@shared-feature/components/modal/peppol-confirmation-modal/peppol-confirmation-modal.component';
import { TaxDatePickerComponent } from '@shared-feature/components/modal/tax-date-picker-modal/tax-date-picker-modal.component';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { finalize, forkJoin, interval, Subscription, takeWhile } from 'rxjs';
import { EInvoiceUuidModalComponent } from 'src/app/components/einvoice/uuid-modal/uuid-modal.component';
import { StockItemEInvoiceRequiredModalComponent } from 'src/app/components/modal/stock-item-einvoice-required-modal/stock-item-einvoice-required-modal.component';
import { RecurringTransactions } from 'src/app/core/constants';
import { FormService } from 'src/app/core/util/form.service';
import { ArdnDetailComponent } from 'src/app/routes/ar/ardn/detail/detail.component';
import { InvDetailComponent } from 'src/app/routes/ar/inv/detail/detail.component';
import { CsDetailComponent } from 'src/app/routes/sale/cs/detail/detail.component';
import { DoDetailComponent } from 'src/app/routes/sale/do/detail/detail.component';
import { SdnDetailComponent } from 'src/app/routes/sale/sdn/detail/detail.component';
import { SinvDetailComponent } from 'src/app/routes/sale/sinv/detail/detail.component';
import { SoDetailComponent } from 'src/app/routes/sale/so/detail/detail.component';
import { DocBaseService } from 'src/app/shared-feature/services/_base/doc-base.service';
import { DocListBaseService } from 'src/app/shared-feature/services/_base/doc-list-base.service';
import { AutoCodeService } from 'src/app/shared-feature/services/auto-code.service';
import { DocEditActionsTabCommunicationService } from 'src/app/shared-feature/services/doc-edit-actions-tab-communication.service';
import { EinvLockFieldsService } from 'src/app/shared-feature/services/einv-lock-fields.service';
import { EinvValidatorsService } from 'src/app/shared-feature/services/einv-validators.service';

import { REVERSE_ACL_MAP_SENDER_TRANSACTION_TYPE } from '@shared-feature/components/email-status-modal/send-email-mapping';
import { EinvoiceConfirmationModalComponent } from '../../modal/einvoice-confirmation-modal/einvoice-confirmation-modal.component';
import { InvoiceEmailModalComponent } from '../../modal/invoice-email-modal/invoice-email-modal.component';
import { RecurringTransactionModalComponent } from '../../modal/recurring-transaction-modal/recurring-transaction-modal.component';
import { ReportTypeItem } from '../preview/preview-doc.component';
import { TransactionSenderComponent } from './components/transaction-sender.component';

@Component({
  selector: 'app-doc-edit-actions',
  templateUrl: './doc-edit-actions.component.html',
  styleUrls: ['./doc-edit-actions.component.less'],
  standalone: false
})
export class DocEditActionsComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private readonly GUID_EMPTY = '00000000-0000-0000-0000-000000000000';
  private cdr = inject(ChangeDetectorRef);
  private einvValidators = inject(EinvValidatorsService);
  private einvLockFields = inject(EinvLockFieldsService);
  private einvControlSub: Subscription | null = null;
  @ViewChild('einvConfirmationModal')
  einvConfirmationModal!: EinvoiceConfirmationModalComponent;
  @ViewChild('uuidModal') uuidModal!: EInvoiceUuidModalComponent;
  @ViewChild('stkEinvReqModal')
  stkEinvReqModal!: StockItemEInvoiceRequiredModalComponent;
  @ViewChild('sender') sender!: TransactionSenderComponent;
  @ViewChild('emailPreview') emailPreview!: InvoiceEmailModalComponent;
  @ViewChild('recurringTransaction')
  recurringTransaction!: RecurringTransactionModalComponent;
  @ViewChild('peppolConfirmationModal')
  peppolConfirmationModal!: PeppolConfirmationModalComponent;

  recurringAcls: string[] = RecurringTransactions.map(m =>
    m.aclKey.toLowerCase()
  );

  saving = false;
  snsVisible: boolean = false;
  snsModel: any = {};

  submitSelectionVisible: boolean = false;

  voidVisible = false;
  voidModel: any = {};

  bouncedVisible = false;
  bouncedModel: any = {};

  trfVisible = false;
  trfModel: any = {};
  trfTitle: string = '';

  processingType = '';
  processingEdit = false;

  pendingCloseUrl: string | null = null; //save and send watcher

  screenWidth: number = window.innerWidth;
  private _reportType = '';
  private _supportParkAndPost = false;

  get reportType() {
    return this._reportType;
  }

  @Input()
  set reportType(value: string) {
    this._reportType = value;
    this.reportTypes = [{ reportType: value }];
  }

  @Input()
  set supportParkAndPost(value: boolean) {
    this._supportParkAndPost = value;
  }

  get supportParkAndPost() {
    return this._supportParkAndPost;
  }

  @HostListener('window:resize')
  onResize() {
    this.screenWidth = window.innerWidth;
  }

  @Input() onlinePayment: boolean = false;
  @Input() viewDropdownHeader: TemplateRef<any> | null = null;
  @Input() editDropdownHeader: TemplateRef<any> | null = null;
  @Input() reportTypes: ReportTypeItem[] = [];
  @Input() reportFilter: any | undefined;
  @Input() allowEdit: boolean = true;
  @Input() allowPreview: boolean = true;
  @Input() allowVoid: boolean = true;
  @Input() allowEinvValidate: boolean = false;
  @Input() allowSend: boolean = true;

  @Input() disableSend: boolean = false;
  @Input() disablePreview: boolean = false;
  @Input() disableEinvValidate: boolean = false;

  @Input() enableCustomValidationForAccountField: boolean = false;
  @Input() CustomValidationForAccountFieldName: string = '';
  @Input() sstReturnCheck: boolean = false;
  @Input() isPostToAR: boolean = false;
  @Input() isPostToAP: boolean = false;
  @Output() readonly eInvStatusChange = new EventEmitter<string>();
  @Input() additionalData: any | undefined;
  private _einvDimmedReason: string | undefined | null;
  get einvDimmedReason() {
    if (this._einvDimmedReason) return this._einvDimmedReason;

    if (!this.canSubmitEInvoiceAcl)
      return 'You do not have permission to submit e-invoice for this document type.';

    if (!this.allowEinvValidate) {
      if (!this.srv.isEInvEnabled)
        return 'E-Invoice is not enabled for this company.';
      if (!this.acl.can(`${this.srv.aclKey}.einv`))
        return 'E-Invoice submission is not available for this document type.';

      const docDate = new Date(this.srv._data?.docDate);
      const effective = this.srv.einvEffectiveDate;
      if (
        effective &&
        !Number.isNaN(docDate.getTime()) &&
        docDate < new Date(effective)
      )
        return 'Document date is before the e-invoice effective date.';

      return 'This document is not eligible for e-invoice submission.';
    }

    if (this.disableEinvValidate)
      return 'E-Invoice full validation is required to submit.';

    if (
      this.srv.parkAndPost &&
      this.supportParkAndPost &&
      this.srv._data.documentStatus < 2
    )
      return 'Document must be approved before submitting e-invoice.';

    if (this.einvSubmitted) return 'E-Invoice is already submitted.';

    if (this.peppolSent) return 'Peppol is already sent.';

    if (this.blockFutureDateSubmission)
      return 'Submission for future-dated or earlier than past 3 days transactions are not allowed';

    const einvControl = this.srv.form.get('eInvoiceControl');
    if (einvControl) {
      if (einvControl.value !== 2)
        return 'E-Invoice fields is not fully validated.';
    }

    const cancelled = this.srv._data.isCancelled;
    if (cancelled) {
      return 'Cancelled transactions cannot be submitted to LHDN.';
    }

    return '';
  }

  get submitEInvoiceDisabled() {
    return this.srv.loading || this.processingEdit || !!this.einvDimmedReason;
  }

  @Input() set einvDimmedReason(value: string) {
    this._einvDimmedReason = value;
  }

  get einvSubmitted() {
    if (this.id === '0') return false;

    // E-Invoice is already submitted, or validated, or cancelled
    if (
      this.srv._data.uin &&
      this.srv._data.eInvoiceStatus &&
      (this.srv._data.eInvoiceStatus === 'Valid' ||
        this.srv._data.eInvoiceStatus === 'Submitted' ||
        this.srv._data.eInvoiceStatus === 'Cancelled')
    )
      return true;

    // Assigned to consolidated einvoice
    if (this.srv._data.consolidatedDocCode) return true;
    return false;
  }

  get blockFutureDateSubmission() {
    const eInvoiceSetting = this.srv.eInvoiceService.eInvoiceSetting;
    const docDateStr = this.srv._data?.docDate;

    if (!docDateStr) return false;

    // Convert docDate string to Date object
    const docDate = new Date(docDateStr);
    // Normalize to start of day for accurate comparison
    docDate.setHours(0, 0, 0, 0);

    if (
      (eInvoiceSetting.submissionDateAs === 0 &&
        eInvoiceSetting.blockFutureDateSubmission) ||
      eInvoiceSetting.submissionDateAs === 1
    ) {
      // Block if docDate is in the future
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Calculate today - 3 days
      const minDate = new Date(today);
      minDate.setDate(today.getDate() - 3);

      // Block if docDate is in the future or earlier than past 3 days
      return docDate > today || docDate < minDate;
    }

    // Otherwise, do not block
    return false;
  }

  get peppolSent() {
    //Peppol is already sent
    if (this.srv._data.peppolStatus && this.srv._data.peppolStatus === 'Sent')
      return true;

    return false;
  }

  get canSubmitEInvoiceAcl() {
    return this.acl.can({
      ability: [`einv-submission-${this.srv.aclKey}.update`, 'einv.enabled'],
      mode: 'allOf'
    });
  }

  /** Show Submit E-Invoice whenever e-invoice applies to this document type (disabled state explains blockers). */
  get showSubmitEInvoiceButton() {
    if (this.srv.editing || this.id === '0') return false;

    const docEinvAcl = `${this.srv.aclKey}.einv`;
    if (!this.acl.can(docEinvAcl)) return false;

    return this.srv.isEInvEnabled || this.acl.can('einv.enabled');
  }

  get isConvertDoc() {
    if (this.srv._data.receivedDocId || this.srv._data.peppolDocId) return true;

    return false;
  }

  get einvControl(): number {
    const einvControl = this.srv.form.get('eInvoiceControl');
    if (einvControl) {
      return einvControl.value;
    }
    return -1;
  }

  @Input() dimmedDelete: boolean = false;
  @Input() options: any = {};
  @Input() onAction!: (action: string) => Promise<any>;
  @Input() validate: ((filter: any, type: any) => Promise<boolean>) | undefined;

  constructor(
    private modal: NzModalService,
    public acl: ACLService,
    private router: Router,
    private route: ActivatedRoute,
    private fs: FormService,
    public listSrv: DocListBaseService,
    public srv: DocBaseService,
    private autoCodeSrv: AutoCodeService,
    private loadingSrv: LoadingService,
    private reuseTabSrv: ReuseTabService,
    private msg: NzMessageService,
    @Optional()
    @Host()
    @SkipSelf()
    private salesInvoiceComponent: SinvDetailComponent,
    @Optional()
    @Host()
    @SkipSelf()
    private cashSalesComponent: CsDetailComponent,
    @Optional()
    @Host()
    @SkipSelf()
    private salesDebitNotesComponent: SdnDetailComponent,
    @Optional()
    @Host()
    @SkipSelf()
    private accountReceivableDebitNotesComponent: ArdnDetailComponent,
    @Optional()
    @Host()
    @SkipSelf()
    private invoiceComponent: InvDetailComponent,
    @Optional()
    @Host()
    @SkipSelf()
    private DeliveryOrderComponent: DoDetailComponent,
    @Optional()
    @Host()
    @SkipSelf()
    private SalesOrderComponent: SoDetailComponent
  ) {
    // this.srv.resetData();
    this.screenWidth = window.innerWidth;

    this.listenEvents();
  }

  get id() {
    return this.route.snapshot.params['id'];
  }

  get isCopyFromAction() {
    return this.route.snapshot.queryParams['type'] === 'FROM';
  }

  get canCreate() {
    if (this.srv.aclKey) {
      return this.acl.can({ ability: [`${this.srv.aclKey}.create`] });
    }

    return true;
  }

  get canEdit() {
    if (this.srv.aclKey) {
      return this.acl.can({ ability: [`${this.srv.aclKey}.update`] });
    }

    return true;
  }

  get canDelete() {
    if (this.srv.aclKey) {
      return this.acl.can({ ability: [`${this.srv.aclKey}.delete`] });
    }

    return true;
  }

  get canApprove() {
    return this.acl.can({
      ability: [`approve-documents-${this.srv.parentMenuAclKey}.update`]
    });
  }

  ngOnInit(): void {
    this.initializeEvents();
  }

  ngAfterViewInit(): void {
    //Nima: Intentionally empty - initializeEvents() is called in ngOnInit to prevent duplicate HTTP requests
    // The duplicate call was causing the component to make two API calls instead of one
    //this.initializeEvents();

    // Process onlinePayment events after ViewChild is initialized
    if (this.srv.eventData && this.srv.eventData.type === 'onlinePayment') {
      const eventData = this.srv.eventData.data;
      if (this.emailPreview) {
        this.emailPreview.show(eventData, this.srv.eventData.option);
      }
    }
    return;
  }

  ngOnDestroy(): void {
    this.einvControlSub?.unsubscribe();
    // Kelvin: no need to call the resetData, because the srv will create a new instance every time open the detail page
    // If we call ResetData, it will call this line "this.modified = false;"
    // And it will trigger "this.reuseTab.closable = !this._modified;"
    // This will tigger another event "closable" changed, in the reuseTabService
    // Because of this event, the closed tab will be attached back again
    // this.srv.resetData();
  }

  showCopyToModal = signal(false);
  showCopyFromModal = signal(false);

  onCopyTo = () => this.showCopyToModal.set(true);
  onCopyFrom = () => this.showCopyFromModal.set(true);

  showCopyToButton = () => {
    return STOCK_RELATED_TRANSACTIONS.includes(this.srv.aclKey);
  };

  showCopyFromButton = () => {
    return (
      this.srv._data.isCancelled === false &&
      this.srv.aclKey !== 'csr' &&
      this.srv.aclKey !== 'cpf' &&
      STOCK_RELATED_TRANSACTIONS.includes(this.srv.aclKey)
    );
  };
  copyTransactionService = inject(CopyTransactionService);
  onApplyCopy(data: any) {
    this.showCopyToModal.set(false);
    this.showCopyFromModal.set(false);
    if (data) {
      // Check if we're in an existing transaction and the action is 'FROM'
      if (this.id !== '0' && data.type === 'FROM') {
        // Fetch the source transaction data and overwrite current transaction
        this.loadingSrv.open();
        this.copyTransactionService
          .getCopyData({
            source: data.source,
            destination: data.destination,
            id: data.id
          })
          .pipe(
            finalize(() => {
              this.loadingSrv.close();
            })
          )
          .subscribe({
            next: (sourceData: any) => {
              // Preserve the current transaction's doc code and ID
              const currentDocCode = this.srv.form.get('docCode')?.value;
              const currentId = this.srv.form.get('id')?.value;

              // Set SupplierInvNo to null for Purchases when copying
              const pAcc = ['pinv', 'pdn'];
              if (pAcc.includes(this.srv.aclKey) && sourceData) {
                sourceData.supplierInvNo = null;
              }

              // Overwrite with source data
              this.editData(sourceData);

              // Restore the current transaction's doc code and ID
              this.srv.form.patchValue({
                docCode: currentDocCode,
                id: currentId
              });

              this.srv.modified = true;
            },
            error: () => {
              this.msg.error('Failed to load source transaction data');
            }
          });
      } else {
        // Navigate to new transaction (existing behavior)
        this.router.navigate([`/${data.routeBase}/detail/0`], {
          queryParams: {
            destination: data.destination,
            source: data.source,
            id: data.id,
            type: data.type,
            isCopy: true
          }
        });
      }
    }
  }

  listenEvents() {
    // Listen event for data cloning
    this.listSrv.eventTrigger.subscribe((event: any) => {
      if (event?.type === 'clone' && this.id == '0') {
        if (event?.key === this.srv.aclKey) {
          this.editData(event?.data);
          this.srv.modified = true;
        }
      }
    });

    // Listen event for other operations
    this.srv.eventTrigger.subscribe((event: any) => {
      if (event?.type === 'OR_TRANSFER') {
        this.editData(event?.data);
        this.srv.modified = true;
      }

      if (event?.type === 'sendTransaction') {
        if (event?.isOnlinePayment) {
          this.emailPreview.show(event.data, event.option);
          this.cdr.detectChanges();
        } else {
          const senderType = this.srv.aclKey != null
            ? REVERSE_ACL_MAP_SENDER_TRANSACTION_TYPE[this.srv.aclKey]
            : this.srv.transactionType;
          this.sender.handle({
            option: event.option,
            type: senderType,
            transactionId: event.data.id
          });
          this.cdr.detectChanges();
        }
      }

      if (event?.type === 'reload') {
        if (this.id === event?.id) {
          this.reload();
        }
      }

      if (event?.type === 'copy') {
        this.editData(event?.data);
        this.srv.modified = true;
      }
    });
  }

  initializeEvents() {
    // Handle pending data for any operations
    if (this.srv.eventData) {
      const eventData = this.srv.eventData.data;
      switch (this.srv.eventData.type) {
        case 'clone':
        case 'transfer':
        case 'copy':
          this.editData(eventData);
          return;

        case 'sendTransaction':
          const senderType = this.srv.aclKey != null
            ? REVERSE_ACL_MAP_SENDER_TRANSACTION_TYPE[this.srv.aclKey]
            : this.srv.transactionType;
          this.sender.handle({
            option: this.srv.eventData.option,
            type: senderType,
            transactionId: this.srv.eventData.data.id
          });
          this.cdr.detectChanges();
          break;

        case 'onlinePayment':
          // onlinePayment events are processed in ngAfterViewInit to ensure ViewChild is ready
          // Don't process here, just leave eventData for ngAfterViewInit to handle
          break;
      }
    }

    // If is existing record, refresh
    if (this.id !== '0') {
      this.reload();
    } else {
      this.loadNew();
    }
  }

  edit() {
    if (this.srv.loading) {
      return;
    }
    this.processingEdit = true;
    this.srv.onAction('edit', this.srv._data).then(() => {
      this.toggleMode('edit');

      const einvControl = this.srv._data.eInvoiceControl;

      // Toggle E-Invoice validators by EInvoice Control
      if (einvControl !== undefined && einvControl !== null)
        this.einvValidators.toggleFormValidators(
          this.srv.form,
          this.srv._data.eInvoiceControl
        );

      this.processingEdit = false;
    });
  }

  async toggleMode(mode: 'view' | 'edit') {
    this.srv.observeChanges = false;
    if (mode === 'edit') {
      this.fs.enable(this.srv.form);
      if ((this.einvSubmitted || this.peppolSent) && !this.isConvertDoc)
        this.einvLockFields.toggleLockField(
          this.srv.form,
          this.einvSubmitted || this.peppolSent
        );
      else if (this.sstReturnCheck && this.srv.form.value.id > 0) {
        let details =
          this.srv.form.value.details?.length > 0
            ? this.srv.form.value.details
            : this.srv.form.value.itemDetails || [];

        // Check if details is empty and use docTaxCode and docTaxCodeId if it is
        if (!details || details.length === 0) {
          if (this.srv.form.value.docTaxCode) {
            details = [
              {
                taxCode: this.srv.form.value.docTaxCode,
                taxCodeId: this.srv.form.value.docTaxCodeId
              }
            ];
          }
        }

        var isValid = await this.validateTaxDate(this.srv.form.value.taxDate, [
          ...details
        ]);
        if (isValid.isValid == false) {
          this.srv.form.controls['taxDate'].disable();
          this.srv.form.controls['itemDetails'].disable();
        }
      }
    } else {
      this.fs.disable(this.srv.form);
    }

    this.einvControlSub?.unsubscribe();
    this.einvControlSub = null;

    if (mode === 'edit') {
      this.einvLockFields.toggleUinField(
        this.srv.form,
        this.srv._data.eInvoiceControl ?? -1,
        this.srv._data.uin,
        this.srv._data.eInvoiceStatus
      );

      const einvCtrl = this.srv.form.get('eInvoiceControl');
      if (einvCtrl) {
        this.einvControlSub = einvCtrl.valueChanges.subscribe(val => {
          this.einvLockFields.toggleUinField(
            this.srv.form,
            val ?? -1,
            this.srv._data.uin,
            this.srv._data.eInvoiceStatus
          );
        });
      }
    }

    this.fs.resetErrors(this.srv.form);
    this.srv.modified = false;
    this.srv.editing = mode === 'edit';
    this.srv.loading = this.saving = this.srv.deleting = false;
    this.srv.observeChanges = mode === 'edit';
  }

  loadNew() {
    if (this.srv.loading === true) return;
    this.srv.loading = true;
    this.loadingSrv.open();
    this.srv
      .new()
      .pipe(
        finalize(() => {
          this.loadingSrv.close();
          this.srv.loading = false;
        })
      )
      .subscribe({
        next: res => {
          this.editData(res);
          this.srv.focus();
        }
      });
  }

  gotoNew() {
    const curUrl = this.reuseTabSrv.curUrl;
    if (curUrl.endsWith('/0')) {
      this.loadNew();
    } else {
      this.router.navigate(['../0'], { relativeTo: this.route }).then(() => {
        this.reuseTabSrv.close(curUrl, true);
      });
    }
  }

  private editData(res: any) {
    this.srv.observeChanges = false;
    if (!this.srv.code && this.autoCodeSrv.activeFormat) {
      res.docCode = `auto-${this.autoCodeSrv.activeFormat?.id}`;
      this.autoCodeSrv.refreshSampleCode();
    }

    if (!res.peppolDocId && !res.receivedDocId) {
      if (res.docDate && !res.isPast) res.docDate = new Date();
      if (res.taxDate && !res.isPast) res.taxDate = new Date();
      if (res.postDate && !res.isPast) res.postDate = new Date();
    }

    this.srv.setData(res);
    this.toggleMode('edit');
    this.updateTitle();
  }

  chooseSender(action: any = null, toSave: boolean = true) {
    if (this.disableSend) return;

    this.snsModel = { toSave: toSave, action: action };

    if (this.onlinePayment) {
      this.snsVisible = true;
    } else {
      if (toSave) this.save(action);
      else
        this.srv.triggerEvent({
          type: 'sendTransaction',
          option: action,
          data: this.srv._data,
          isOnlinePayment: false
        });
    }
  }

  promptSubmitModel() {
    if (this.submitEInvoiceDisabled) return;

    const customer = this.srv._data.customer;
    const supplier = this.srv._data.supplier;

    if (this.acl.canAbility('m2.65536')) {
      if (customer) {
        if (customer.isPeppolConnect) this.submitSelectionVisible = true;
        else this.einvValidation();
      } else if (supplier) {
        if (supplier.isPeppolConnect) this.submitSelectionVisible = true;
        else this.einvValidation();
      } else this.einvValidation();
    } else this.einvValidation();
  }

  confirmSubmitEinvBy(action: any) {
    this.submitSelectionVisible = false;

    if (action == 'peppol')
      this.peppolValidation(); //qne
    else this.einvValidation();
  }

  confirmSender(action: any, isPayment: boolean) {
    this.snsVisible = false;
    if (this.snsModel.toSave) {
      this.save(action, isPayment);
    } else {
      this.srv.triggerEvent({
        type: 'sendTransaction',
        option: action,
        data: this.srv._data,
        isOnlinePayment: isPayment
      });
    }
  }

  /**
   * Helper method to clean nullable Guid fields by converting empty strings to null
   */
  private cleanNullableGuidFields(body: any): void {
    if (!body) return;

    body.bankChargesAccountId = this.normalizeNullableGuidValue(
      body.bankChargesAccountId
    );

    // BundleId is nullable Guid on backend detail DTOs.
    // Some flows may still produce empty/non-guid values and break model binding.
    if (Array.isArray(body.itemDetails)) {
      body.itemDetails = body.itemDetails.map((line: any) => {
        if (!line || typeof line !== 'object') return line;
        return {
          ...line,
          id: this.normalizeRequiredGuidValue(line.id),
          bundleId: this.normalizeNullableGuidValue(line.bundleId)
        };
      });
    }
  }

  private normalizeNullableGuidValue(value: any): string | null {
    if (value == null || value === '' || value === 0) return null;
    if (typeof value !== 'string') return null;

    const normalized = this.sanitizeGuidString(value);
    if (!normalized) return null;
    if (normalized === this.GUID_EMPTY) return null;

    return this.isGuid(normalized) ? normalized : null;
  }

  private normalizeRequiredGuidValue(value: any): string {
    if (value == null || value === '' || value === 0) return this.GUID_EMPTY;
    if (typeof value !== 'string') return this.GUID_EMPTY;

    const normalized = this.sanitizeGuidString(value);
    if (!normalized) return this.GUID_EMPTY;

    return this.isGuid(normalized) ? normalized : this.GUID_EMPTY;
  }

  private sanitizeGuidString(value: string): string {
    return value.trim().replace(/^\{|\}$/g, '');
  }

  private isGuid(value: string): boolean {
    // Accept any canonical GUID shape (SQL Server/.NET GUIDs are not always RFC variant/version constrained).
    const guidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(value);
  }

  private normalizeBundleLinksBeforeSave(): void {
    const itemDetailsArray = this.srv.form.get(
      'itemDetails'
    ) as FormArray | null;
    if (itemDetailsArray) {
      this.srv.bundleHelper.ensureBundleIds(itemDetailsArray);
    }
  }

  private buildSaveBody(): any {
    this.normalizeBundleLinksBeforeSave();
    const body = { ...this.srv._data };
    Object.assign(body, this.srv.form.getRawValue());
    this.cleanNullableGuidFields(body);
    return body;
  }

  async handleSaveWithModal(
    saveMethod: (
      action?: any,
      isPayment?: any,
      documentStatus?: number
    ) => void,
    action?: any,
    isPayment: boolean = false,
    documentStatus: number = 0
  ) {
    const body = this.buildSaveBody();

    // Check if the customerId is set and if the component is present
    const shouldShowModal =
      body.customerId &&
      !!(
        this.salesInvoiceComponent ||
        this.cashSalesComponent ||
        this.salesDebitNotesComponent ||
        this.accountReceivableDebitNotesComponent ||
        this.invoiceComponent ||
        this.SalesOrderComponent ||
        this.DeliveryOrderComponent
      );

    if (this.enableCustomValidationForAccountField) {
      var isValid = this.validateAccountForDepositToOrPaymentBy();
      if (!isValid) return;
    }

    if (shouldShowModal) {
      let confirmed = false;
      if (this.cashSalesComponent) {
        confirmed = await this.showCreditLimitModal(
          body.docDate,
          body.currency.code,
          body.netTotalAmount,
          'CS',
          body.customerId,
          this.id != 0 ? this.srv._data.netTotalAmount : 0
        );
      } else if (this.DeliveryOrderComponent) {
        confirmed = await this.showCreditLimitModal(
          body.docDate,
          body.currency.code,
          body.netTotalAmount,
          'DO',
          body.customerId,
          this.id != 0 ? this.srv._data.netTotalAmount : 0
        );
      } else if (this.SalesOrderComponent) {
        confirmed = await this.showCreditLimitModal(
          body.docDate,
          body.currency.code,
          body.netTotalAmount,
          'SO',
          body.customerId,
          this.id != 0 ? this.srv._data.netTotalAmount : 0
        );
      } else {
        confirmed = await this.showCreditLimitModal(
          body.docDate,
          body.currency.code,
          body.netTotalAmount,
          body.docType,
          body.customerId,
          this.id != 0 ? this.srv._data.netTotalAmount : 0
        );
      }
      if (!confirmed) {
        return; // Stop the process if the user cancels
      }
    }

    // Call the actual save method
    saveMethod.call(this, action, isPayment, documentStatus);
  }

  validateAccountForDepositToOrPaymentBy() {
    if (
      this.srv.form.controls['isMultiPayment'] &&
      !this.srv.form.controls['isMultiPayment'].value &&
      this.srv.form.controls['account'] &&
      !this.srv.form.controls['account'].value &&
      this.srv.form.controls['isPostToAR'] &&
      !this.srv.form.controls['isPostToAR'].value
    ) {
      this.msg.error(
        `${this.CustomValidationForAccountFieldName} is required. Select an account and proceed with the saving.`
      );
      this.srv.form.controls['account'].markAsTouched();
      this.srv.form.controls['account'].setErrors({ required: true });
      this.srv.observeChanges = true;
      this.saving = false;
      this.cdr.detectChanges();
      return false;
    } else if (
      this.srv.form.controls['isMultiPayment'] &&
      !this.srv.form.controls['isMultiPayment'].value &&
      this.srv.form.controls['account'] &&
      !this.srv.form.controls['account'].value &&
      this.srv.form.controls['isPostToAP'] &&
      !this.srv.form.controls['isPostToAP'].value
    ) {
      this.msg.error(
        `${this.CustomValidationForAccountFieldName} is required. Select an account and proceed with the saving.`
      );
      this.srv.form.controls['account'].markAsTouched();
      this.srv.form.controls['account'].setErrors({ required: true });
      this.srv.observeChanges = true;
      this.saving = false;
      this.cdr.detectChanges();
      return false;
    } else if (
      this.srv.form.controls['isMultiPayment'] &&
      !this.srv.form.controls['isMultiPayment'].value &&
      this.srv.form.controls['account'] &&
      !this.srv.form.controls['account'].value &&
      !this.srv.form.controls['isPostToAP'] &&
      !this.srv.form.controls['isPostToAR']
    ) {
      this.msg.error(
        `${this.CustomValidationForAccountFieldName} is required. Select an account and proceed with the saving.`
      );
      this.srv.form.controls['account'].markAsTouched();
      this.srv.form.controls['account'].setErrors({ required: true });
      this.srv.observeChanges = true;
      this.saving = false;
      this.cdr.detectChanges();
      return false;
    }
    return true;
  }

  save(action: any = null, isPayment: boolean = false) {
    this.handleSaveWithModal(this.saveProcess, action, isPayment);
  }

  saveProcess(action: any = null, isPayment: boolean = false) {
    this.srv.observeChanges = false;
    if ((this.einvSubmitted || this.peppolSent) && !this.isConvertDoc)
      this.einvLockFields.toggleLockField(this.srv.form, false);
    this.saving = true;

    this.srv.validateAsync().then(async (valid: boolean) => {
      if (!valid) {
        // if (this.srv.form.get('docCode')?.hasError('required')) {
        //   this.msg.error('Doc Code is required');
        // }
        this.srv.observeChanges = true;
        this.saving = false;
        this.cdr.detectChanges();
        return;
      }

      // check and change einvcontrol
      const isSelfBilled = this.srv.form.value.isSelfBilled;
      const itemDetails = this.srv.form.value.itemDetails;
      const aclKey = this.srv.aclKey;

      const proceed = await this.validateAndCheckStock(
        itemDetails,
        aclKey,
        isSelfBilled
      );
      if (!proceed) {
        this.saving = false;
        return;
      }
      // check and change einvcontrol ended

      this.srv
        .onAction('save', this.srv._data)
        .then(async () => {
          const body = this.buildSaveBody();

          if (this.sstReturnCheck) {
            const sstPassed = await this.performFullSSTCheck(body);
            if (!sstPassed) {
              this.saving = false;
              this.srv.observeChanges = true;
              this.cdr.detectChanges();
              return;
            }
          }

          const attachmentChanges = this.srv.getAttachmentChangesPayload
            ? this.srv.getAttachmentChangesPayload()
            : null;
          if (attachmentChanges) {
            body.attachmentChanges = attachmentChanges;
          }

          // reassign currencyId
          if (body.currency?.id) {
            body.currencyId = body.currency.id;
          }
          this.srv.loading = this.saving = true;

          if (this.id === '0') {
            this.srv
              .create(body)
              .pipe(
                finalize(() => {
                  this.srv.loading = this.saving = false;
                  this.srv.observeChanges = true;
                  this.cdr.detectChanges();
                })
              )

              .subscribe({
                next: (res: any) => {
                  this.listSrv.addRow(res);
                  if (!action) {
                    this.clearPathFromQueryStore();

                    this.router.navigate([`../${res.id}`], {
                      relativeTo: this.route
                    });
                    const curUrl = this.reuseTabSrv.curUrl;
                    this.srv.notifyInstantData(res);

                    if (attachmentChanges) {
                      this.srv.clearAttachmentChanges();
                    }

                    this.router
                      .navigate([`../${res.id}`], { relativeTo: this.route })
                      .then(() => {
                        this.reuseTabSrv.close(curUrl);
                      });
                    this.srv.observeChanges = false;
                    this.srv.setData(res);
                    this.fs.disable(this.srv.form);
                    this.srv.modified = false;
                    this.updateTitle();
                    this.srv.triggerEvent({ type: 'save', id: res.id });
                    this.endLoading();
                  } else {
                    const oldUrl = this.reuseTabSrv.curUrl;
                    this.clearPathFromQueryStore();
                    this.srv.notifyInstantData(res);

                    if (attachmentChanges) {
                      this.srv.clearAttachmentChanges();
                    }

                    this.router
                      .navigate([`../${res.id}`], {
                        relativeTo: this.route,
                        state: {
                          sendOption: action,
                          isOnlinePayment: isPayment,
                          data: res
                        }
                      })
                      .then(() => {
                        //close detail/0
                        this.pendingCloseUrl = oldUrl;
                      });
                    this.srv.observeChanges = false;
                    this.srv.setData(res);
                    this.fs.disable(this.srv.form);
                    this.srv.modified = false;
                    this.updateTitle();
                    this.srv.triggerEvent({ type: 'save', id: res.id });
                    this.endLoading();
                  }
                }
              });
          } else {
            this.srv
              .update(body)
              .pipe(
                finalize(() => {
                  this.srv.loading = this.saving = false;
                  this.srv.observeChanges = true;
                  this.cdr.detectChanges();
                })
              )
              .subscribe({
                next: res => {
                  this.clearPathFromQueryStore();
                  this.listSrv.updateRow(res);

                  if (attachmentChanges) {
                    this.srv.clearAttachmentChanges();
                  }

                  this.srv.observeChanges = false;
                  this.srv.setData(res);
                  this.fs.disable(this.srv.form);
                  this.srv.modified = false;

                  this.updateTitle();
                  this.endLoading();
                  if (action) {
                    this.srv.triggerEvent({
                      type: 'sendTransaction',
                      option: action,
                      data: res,
                      isOnlinePayment: isPayment
                    });
                  }
                }
              });
          }
        })
        .catch(() => {
          this.clearPathFromQueryStore();
          this.srv.loading = this.saving = false;
          this.srv.observeChanges = true;
          this.cdr.detectChanges();
        });
    });
  }

  endLoading() {
    this.srv.editing = false;
    this.srv.loading = this.saving = false;
  }

  saveAndDraft(action: any = null, isPayment: boolean = false) {
    this.handleSaveWithModal(this.saveAndApproveProcess, action, isPayment, 0);
  }

  submitForApproval(action: any = null, isPayment: boolean = false) {
    this.handleSaveWithModal(this.saveAndApproveProcess, action, isPayment, 1);
  }

  saveAndApprove(action: any = null, isPayment: boolean = false) {
    this.handleSaveWithModal(this.saveAndApproveProcess, action, isPayment, 2);
  }

  saveAndApproveProcess(
    action: any = null,
    isPayment: boolean = false,
    documentStatus: number = 0
  ) {
    this.srv.observeChanges = false;
    if (this.einvSubmitted || this.peppolSent)
      this.einvLockFields.toggleLockField(this.srv.form, false);
    this.saving = true;
    this.srv.validateAsync().then(async (valid: boolean) => {
      if (!valid) {
        if (this.srv.form.get('docCode')?.hasError('required')) {
          this.msg.error('Doc Code is required');
        }
        this.srv.observeChanges = true;
        this.saving = false;
        this.cdr.detectChanges();
        return;
      }

      // check and change einvcontrol
      const isSelfBilled = this.srv.form.value.isSelfBilled;
      const itemDetails = this.srv.form.value.itemDetails;
      const aclKey = this.srv.aclKey;

      const proceed = await this.validateAndCheckStock(
        itemDetails,
        aclKey,
        isSelfBilled
      );
      if (!proceed) {
        this.saving = false;
        return;
      }

      this.srv.onAction('save', this.srv._data).then(async () => {
        this.saving = true;
        this.srv.loading = true;
        const body = this.buildSaveBody();

        body.documentStatus = documentStatus;
        const attachmentChanges = this.srv.getAttachmentChangesPayload
          ? this.srv.getAttachmentChangesPayload()
          : null;
        if (attachmentChanges) {
          body.attachmentChanges = attachmentChanges;
        }

        // Check if details is empty and use docTaxCode and docTaxCodeId if it is
        if (!body.details || body.details.length === 0) {
          if (body.docTaxCode) {
            body.details = [
              {
                docTaxCode: body.docTaxCode,
                docTaxCodeId: body.docTaxCodeId
              }
            ];
          }
        }

        // reassign currencyId
        if (body.currency?.id) {
          body.currencyId = body.currency.id;
        }

        if (this.sstReturnCheck) {
          const sstPassed = await this.performFullSSTCheck(body);
          if (!sstPassed) {
            this.saving = false;
            this.srv.loading = false;
            this.srv.observeChanges = true;
            this.cdr.detectChanges();
            return;
          }
        }

        if (this.id === '0') {
          this.srv
            .create(body)
            .pipe(
              finalize(() => {
                this.srv.loading = this.saving = false;
                this.srv.observeChanges = true;
                this.cdr.detectChanges();
              })
            )
            .subscribe({
              next: res => {
                this.listSrv.addRow(res);
                this.srv.notifyInstantData(res);
                this.srv.triggerEvent({ type: 'save', id: res.id });
                this.saving = false;
                this.srv.loading = false;
                this.srv.modified = false;
                if (attachmentChanges) {
                  this.srv.clearAttachmentChanges();
                }
                this.clearPathFromQueryStore();
                this.closeReuseTab();
              },
              error: () => {
                this.saving = false;
                this.srv.loading = false;
              }
            });
        } else {
          this.srv
            .update(body)
            .pipe(
              finalize(() => {
                this.srv.loading = this.saving = false;
                this.srv.observeChanges = true;
                this.cdr.detectChanges();
              })
            )
            .subscribe({
              next: res => {
                this.listSrv.updateRow(res);
                this.saving = false;
                this.srv.loading = false;
                this.srv.modified = false;
                if (attachmentChanges) {
                  this.srv.clearAttachmentChanges();
                }
                this.closeReuseTab();
                if (action) {
                  this.srv.triggerEvent({
                    type: 'sendTransaction',
                    option: action,
                    data: res,
                    isOnlinePayment: isPayment
                  });
                }
              },
              error: () => {
                this.saving = false;
                this.srv.loading = false;
              }
            });
        }
      });
    });
  }

  saveAndClose(action: any = null, isPayment: boolean = false) {
    this.handleSaveWithModal(this.saveAndCloseProcess, action, isPayment);
  }

  saveAndCloseProcess() {
    this.srv.observeChanges = false;
    if (this.einvSubmitted || this.peppolSent)
      this.einvLockFields.toggleLockField(this.srv.form, false);
    this.saving = true;
    this.srv.validateAsync().then(async (valid: boolean) => {
      this.saving = true;
      if (!valid) {
        if (this.srv.form.get('docCode')?.hasError('required')) {
          this.msg.error('Doc Code is required');
        }
        this.srv.observeChanges = true;
        this.saving = false;
        this.cdr.detectChanges();
        return;
      }

      // check and change einvcontrol
      const isSelfBilled = this.srv.form.value.isSelfBilled;
      const itemDetails = this.srv.form.value.itemDetails;
      const aclKey = this.srv.aclKey;

      const proceed = await this.validateAndCheckStock(
        itemDetails,
        aclKey,
        isSelfBilled
      );
      if (!proceed) {
        this.saving = false;
        return;
      }
      // check and change einvcontrol ended

      this.srv.onAction('save', this.srv._data).then(async () => {
        this.saving = true;
        this.srv.loading = true;
        const body = this.buildSaveBody();

        // Check if details is empty and use docTaxCode and docTaxCodeId if it is
        if (!body.details || body.details.length === 0) {
          if (body.docTaxCode) {
            body.details = [
              {
                docTaxCode: body.docTaxCode,
                docTaxCodeId: body.docTaxCodeId
              }
            ];
          }
        }

        const attachmentChanges = this.srv.getAttachmentChangesPayload
          ? this.srv.getAttachmentChangesPayload()
          : null;
        if (attachmentChanges) {
          body.attachmentChanges = attachmentChanges;
        }

        // reassign currencyId
        if (body.currency?.id) {
          body.currencyId = body.currency.id;
        }

        if (this.sstReturnCheck) {
          const sstPassed = await this.performFullSSTCheck(body);
          if (!sstPassed) {
            this.saving = false;
            this.srv.loading = false;
            this.srv.observeChanges = true;
            this.cdr.detectChanges();
            return;
          }
        }

        if (this.id === '0') {
          this.srv
            .create(body)
            .pipe(
              finalize(() => {
                this.srv.loading = this.saving = false;
                this.srv.observeChanges = true;
                this.cdr.detectChanges();
              })
            )
            .subscribe({
              next: res => {
                this.listSrv.addRow(res);
                this.srv.notifyInstantData(res);
                this.srv.triggerEvent({ type: 'save', id: res.id });
                this.saving = false;
                this.srv.loading = false;
                this.srv.modified = false;
                if (attachmentChanges) {
                  this.srv.clearAttachmentChanges();
                }
                this.close();
              },
              error: () => {
                this.saving = false;
                this.srv.loading = false;
              }
            });
        } else {
          this.srv
            .update(body)
            .pipe(
              finalize(() => {
                this.srv.loading = this.saving = false;
                this.srv.observeChanges = true;
                this.cdr.detectChanges();
              })
            )
            .subscribe({
              next: res => {
                this.listSrv.updateRow(res);
                this.saving = false;
                this.srv.loading = false;
                this.srv.modified = false;
                if (attachmentChanges) {
                  this.srv.clearAttachmentChanges();
                }
                this.close();
              },
              error: () => {
                this.saving = false;
                this.srv.loading = false;
              }
            });
        }
      });
    });
  }

  saveAndNew(action: any = null, isPayment: boolean = false) {
    this.handleSaveWithModal(this.saveAndNewProcess, action, isPayment);
  }

  saveAndNewProcess() {
    this.srv.observeChanges = false;
    if (this.einvSubmitted || this.peppolSent)
      this.einvLockFields.toggleLockField(this.srv.form, false);
    this.saving = true;
    this.srv.validateAsync().then(async (valid: boolean) => {
      if (!valid) {
        if (this.srv.form.get('docCode')?.hasError('required')) {
          this.msg.error('Doc Code is required');
        }
        this.srv.observeChanges = true;
        this.saving = false;
        this.cdr.detectChanges();
        return;
      }

      // check and change einvcontrol
      const isSelfBilled = this.srv.form.value.isSelfBilled;
      const itemDetails = this.srv.form.value.itemDetails;
      const aclKey = this.srv.aclKey;

      const proceed = await this.validateAndCheckStock(
        itemDetails,
        aclKey,
        isSelfBilled
      );
      if (!proceed) {
        this.saving = false;
        return;
      }
      // check and change einvcontrol ended

      this.srv.onAction('save', this.srv._data).then(async () => {
        this.saving = true;
        this.srv.loading = true;
        const body = this.buildSaveBody();
        body.docDate = new Date(body.docDate).toISOString();

        const attachmentChanges = this.srv.getAttachmentChangesPayload
          ? this.srv.getAttachmentChangesPayload()
          : null;
        if (attachmentChanges) {
          body.attachmentChanges = attachmentChanges;
        }

        // Check if details is empty and use docTaxCode and docTaxCodeId if it is
        if (!body.details || body.details.length === 0) {
          if (body.docTaxCode) {
            body.details = [
              {
                docTaxCode: body.docTaxCode,
                docTaxCodeId: body.docTaxCodeId
              }
            ];
          }
        }

        // reassign currencyId
        if (body.currency?.id) {
          body.currencyId = body.currency.id;
        }

        if (this.sstReturnCheck) {
          const sstPassed = await this.performFullSSTCheck(body);
          if (!sstPassed) {
            this.saving = false;
            this.srv.loading = false;
            this.srv.observeChanges = true;
            this.cdr.detectChanges();
            return;
          }
        }

        if (this.id === '0') {
          this.srv
            .create(body)
            .pipe(
              finalize(() => {
                this.srv.loading = this.saving = false;
                this.srv.observeChanges = true;
                this.cdr.detectChanges();
              })
            )
            .subscribe({
              next: res => {
                this.srv.notifyInstantData(res);
                this.srv.triggerEvent({ type: 'save', id: res.id });
                this.listSrv.addRow(res);
                this.srv.loading = this.saving = false;
                if (attachmentChanges) {
                  this.srv.clearAttachmentChanges();
                }
                this.gotoNew();
              },
              error: () => {
                this.srv.loading = this.saving = false;
              }
            });
        } else {
          this.srv
            .update(body)
            .pipe(
              finalize(() => {
                this.srv.loading = this.saving = false;
                this.srv.observeChanges = true;
                this.cdr.detectChanges();
              })
            )
            .subscribe({
              next: res => {
                this.listSrv.updateRow(res);
                this.srv.loading = this.saving = false;
                if (attachmentChanges) {
                  this.srv.clearAttachmentChanges();
                }
                this.gotoNew();
              },
              error: () => {
                this.srv.loading = this.saving = false;
              }
            });
        }
      });
    });
  }

  submitEInvoice = (options: any): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
      this.srv.http
        .post(
          'api/einvoices/submitDocument',
          {
            id: this.srv.id,
            docType: this.srv.aclKey?.toUpperCase(),
            ...options
          },
          { _raw: true }
        )
        .subscribe({
          next: (resp: any) => {
            this.srv.observeChanges = false;
            this.srv.editing = false;
            this.srv.loading = this.saving = false;
            this.fs.disable(this.srv.form);
            this.srv.modified = false;
            this.reload();

            if (resp.success) {
              this.msg.success(resp.message);
            } else {
              this.msg.error(resp.message);
            }
            const data = resp.data;
            if (data?.acceptedDocuments?.length) {
              const uuid = data.acceptedDocuments[0].uuid;
              this.uuidModal.showLhdnDocument(uuid);
            }

            resolve(true);
          },
          error: () => {
            reject(false);
          }
        });
    });
  };

  cancelEInvoice = (cancellationRemark: string): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
      this.srv.http
        .post(
          'api/einvoices/cancelDocument',
          {
            id: this.srv.id,
            docType: this.srv.aclKey?.toUpperCase(),
            cancelledRemark: cancellationRemark
          },
          { _raw: true }
        )
        .subscribe({
          next: (resp: any) => {
            this.srv.observeChanges = false;
            this.srv.editing = false;
            this.srv.loading = this.saving = false;
            this.fs.disable(this.srv.form);
            this.srv.modified = false;
            this.reload();

            if (resp.success) {
              this.msg.success(resp.message);
            } else {
              this.msg.error(resp.message);
            }
            resolve(true);
          },
          error: () => {
            reject(false);
          }
        });
    });
  };

  rejectEInvoice = (): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
      this.srv.http
        .post(
          'api/einvoices/rejectDocument',
          { id: this.srv.id, docType: this.srv.aclKey?.toUpperCase() },
          { _raw: true }
        )
        .subscribe({
          next: (resp: any) => {
            this.srv.observeChanges = false;
            this.srv.editing = false;
            this.srv.loading = this.saving = false;
            this.fs.disable(this.srv.form);
            this.srv.modified = false;
            this.reload();

            if (resp.success) {
              this.msg.success(resp.message);
            } else {
              this.msg.error(resp.message);
            }
            resolve(true);
          },
          error: () => {
            reject(false);
          }
        });
    });
  };

  einvValidation() {
    if (!this.srv.aclKey) return;

    // Validate phone and email before showing confirmation modal
    const form = this.srv.form;
    let validationErrors: string[] = [];

    // Get phone field value (different field names for different document types)
    let phoneValue = '';
    if (form.get('customerPhone')?.value) {
      phoneValue = form.get('customerPhone')?.value;
    } else if (form.get('phone1')?.value) {
      phoneValue = form.get('phone1')?.value;
    }

    // Get email field value
    const emailValue = form.get('email')?.value || '';

    // Phone validation
    if (phoneValue && !this.isValidPhone(phoneValue)) {
      validationErrors.push(
        'The mobile phone number is not formatted correctly. Requirements: minimum of 8 characters, maximum of 20 characters, only numbers and optional plus sign (+) allowed at the beginning, no spaces between numbers.'
      );
    }

    // Email validation
    if (emailValue && !this.isValidEmail(emailValue)) {
      validationErrors.push(
        'The email address is not in the correct format. Requirements: Must conform to valid formats (RFC 5321 & RFC 5322), maximum 320 characters, no spaces between characters.'
      );
    }

    // Show validation errors if any
    if (validationErrors.length > 0) {
      this.msg.error(validationErrors.join('\n'));
      return;
    }

    // Any E-Invoice validation should be performed during the confirmation, there should not be any UI validation
    // As the E-Invoice submission should only perform in view mode

    this.einvConfirmationModal.show(this.srv.id, this.srv.aclKey);
  }

  private isValidPhone(phone: string): boolean {
    if (!phone || phone.trim() === '') return false;

    phone = phone.trim();

    // Requirement 3: No space between the digits
    if (phone.includes(' ')) return false;

    // Requirement 2: Only allow optional plus (+) symbol at the front of the number
    if (phone.startsWith('+')) {
      // Remove the + sign for length and digit validation
      const phoneWithoutPlus = phone.substring(1);

      // Check if the rest are digits or parentheses
      if (!/^[\d()]+$/.test(phoneWithoutPlus)) return false;

      // Requirement 1: Minimum 8 and maximum 20 characters (including +)
      if (phone.length < 8 || phone.length > 20) return false;
    } else {
      // Check if all characters are digits or parentheses
      if (!/^[\d()]+$/.test(phone)) return false;

      // Requirement 1: Minimum 8 and maximum 20 characters
      if (phone.length < 8 || phone.length > 20) return false;
    }

    return true;
  }

  private isValidEmail(email: string): boolean {
    if (!email || email.trim() === '') return false;

    email = email.trim();

    // Requirement 2: Maximum 320 characters
    if (email.length > 320) return false;

    // Requirement 3: No space between the characters
    if (email.includes(' ')) return false;

    // Requirement 1: Must follow valid format (RFC 5321 & RFC 5322)
    try {
      if (!email.includes('@')) return false;

      // Simple email validation using regex similar to RFC format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    } catch {
      return false;
    }
  }

  peppolValidation() {
    if (!this.srv.aclKey) return;

    this.peppolConfirmationModal.show(this.srv.id, this.srv.aclKey);
  }

  submitPeppol = (): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
      this.srv.http
        .post(
          'api/StorecovePeppol/SubmitDocument',
          {
            id: this.srv.id,
            docType: this.srv.aclKey?.toUpperCase(),
            docCode: this.srv.form.get('docCode')!.value
          },
          { _raw: true }
        )
        .subscribe({
          next: (resp: any) => {
            this.srv.observeChanges = false;
            this.srv.editing = false;
            this.srv.loading = this.saving = false;
            this.fs.disable(this.srv.form);
            this.srv.modified = false;
            this.reload();

            if (resp.success) {
              this.msg.success(resp.message);
            } else {
              this.msg.error(resp.message);
            }
            // const data = resp.data;

            resolve(true);
          },
          error: () => {
            reject(false);
          }
        });
    });
  };

  async validateAndCheckStock(
    itemDetails: any,
    aclKey: string,
    isSelfBilled: boolean
  ): Promise<boolean> {
    // Malaysia e-Invoice rules only apply when the feature is enabled (country === 'MY').
    if (!this.srv.isEInvEnabled) return true;

    const salesAclKeys = ['sinv', 'sdn', 'scn', 'cs'];
    const selfBilledAclKeys = ['pinv', 'pdn', 'pcn', 'cp'];

    const isAclKeyValid =
      salesAclKeys.includes(aclKey) ||
      (selfBilledAclKeys.includes(aclKey) && isSelfBilled);

    if (!isAclKeyValid) return true; // No need to proceed if ACL key is not valid

    const withStock = itemDetails?.filter((item: any) => item.stock);

    // Proceed only if there are rows with valid stock
    const allStockRequiredEInvoice =
      withStock?.length > 0 &&
      withStock.some((item: any) => item.stock.isRequiredEInvoice === true);

    if (allStockRequiredEInvoice) {
      return await this.validateEInvStockReq();
    }

    return true;
  }

  validateEInvStockItem() {
    const isSelfBilled = this.srv.form.value.isSelfBilled;
    const itemDetails = this.srv.form.value.itemDetails;
    const aclKey = this.srv.aclKey;

    const salesAclKeys = ['sinv', 'sdn', 'scn', 'cs'];
    const selfBilledAclKeys = ['pinv', 'pdn', 'pcn', 'cp'];

    const isAclKeyValid =
      salesAclKeys.includes(aclKey) ||
      (selfBilledAclKeys.includes(aclKey) && isSelfBilled);
    if (isAclKeyValid) {
      const withStock =
        itemDetails && itemDetails.filter((item: any) => item.stock);
    }
  }

  validateEInvStockReq(): Promise<boolean> {
    return new Promise(resolve => {
      const eInvoiceControl = this.srv.form.value.eInvoiceControl;

      if (
        eInvoiceControl !== 2 &&
        eInvoiceControl !== 3 &&
        eInvoiceControl !== -1
      ) {
        this.stkEinvReqModal.show();

        // Listen for confirmation from modal
        const modal = this.stkEinvReqModal.confirm.subscribe(
          (result: boolean) => {
            if (result) {
              this.srv.observeChanges = true;
              this.srv.form.patchValue({ eInvoiceControl: 2 }); // Switch to full validation
              this.srv.observeChanges = false;
              this.srv.validateAsync().then((revalid: boolean) => {
                resolve(revalid); // Proceed only if revalidation passes
              });
            } else {
              this.srv.observeChanges = true;
              resolve(false); // Cancel
            }
            modal.unsubscribe();
          }
        );
      } else {
        resolve(true); // Proceed if no validation is needed
      }
    });
  }

  clone() {
    const data = this.srv.clone();
    if (data) {
      //this.srv.observeChanges = false;
      //this.srv.setData(data);
      //this.toggleMode('edit');

      // if (this.srv.parkAndPost) {
      //   data.documentStatus = 0;
      // }
      if (data.isClosed) {
        data.isClosed = false;
        data.closedDate = null;
      }
      this.clearPathFromQueryStore();
      this.router.navigate(['../0'], {
        state: { key: this.srv.aclKey, cloneData: true, data: data },
        relativeTo: this.route
      });
    }
  }

  clearPathFromQueryStore() {
    console.log((window as any).__tabQueryStore);
    const currUrl = this.router.url;
    const path = `${currUrl.substring(0, currUrl.lastIndexOf('/'))}/0`;
    console.log(path);
    // Clean up saved query parameters (like basic.component does)
    const store: Map<string, string> | undefined = (window as any)
      .__tabQueryStore;
    if (store) {
      store.delete(path);
    }
  }

  handleVoidClick() {
    if (this.srv._data.consolidatedDocCode) return;

    this.confirm(
      'Cancel Transaction',
      'Are you sure you want to cancel this transaction?'
    ).then(() => {
      this.showVoidModal();
    });
  }

  showVoidModal() {
    this.srv
      .onAction('void', this.srv._data)
      .then(() => {
        this.voidVisible = true;
        this.voidModel = {
          id: this.srv._data.id,
          cancelledDate: new Date(),
          cancelledRemark: ''
        };
        this.cdr.detectChanges();
      })
      .catch(() => {
        this.reload();
      });
  }

  showCloneModal() {
    if (this.isConvertDoc) return;

    this.confirm(
      'Clone Transaction',
      'Would you like to clone this transaction to proceed with another e-Invoice resubmission?'
    ).then(() => {
      this.clone();
    });
  }

  void() {
    this.processingType = 'void';
    this.srv
      .void(this.voidModel)
      .then(res => {
        this.srv.observeChanges = false;
        this.srv.editing = false;
        this.processingType = '';
        this.srv.setData(res);
        this.fs.disable(this.srv.form);
        this.srv.modified = false;
        this.voidVisible = false;
        this.voidModel = {};

        if (res.eInvoiceStatus == 'Cancelled') this.showCloneModal();
      })
      .catch(() => {
        this.processingType = '';
      });
  }

  handleDevoidClick() {
    if (this.srv._data.eInvoiceStatus == 'Cancelled' && this.isConvertDoc)
      return;

    this.confirm(
      'Undo Cancel Transaction',
      'Are you sure you want to undo the cancel of this transaction?'
    ).then(() => {
      this.devoid();
    });
  }

  devoid() {
    this.srv.loading = true;
    this.srv.devoid(this.srv._data.id).subscribe({
      next: res => {
        this.srv.observeChanges = false;
        this.srv.editing = false;
        this.srv.loading = false;
        this.srv.setData(res);
        this.fs.disable(this.srv.form);
        this.srv.modified = false;
      },
      error: () => {
        this.srv.loading = false;
      }
    });
  }

  handleBounceMarkClick() {
    this.confirm(
      'Bounced Cheque',
      'Are you sure you want to mark this transaction as bounced cheque?'
    ).then(() => {
      this.showBouncedModal();
    });
  }

  showBouncedModal() {
    this.srv
      .onAction('setBounced', this.srv._data)
      .then(() => {
        this.bouncedVisible = true;
        this.bouncedModel = {
          id: this.srv._data.id,
          bouncedChequeDate: new Date()
        };
        this.cdr.detectChanges();
      })
      .catch(() => {});
  }

  setBounced() {
    this.processingType = 'bounced';
    this.srv
      .setBounced(this.bouncedModel)
      .pipe(
        finalize(() => {
          this.processingType = '';
        })
      )
      .subscribe({
        next: res => {
          this.srv.observeChanges = false;
          this.srv.editing = false;
          this.srv.setData(res);
          this.fs.disable(this.srv.form);
          this.srv.modified = false;
          this.bouncedVisible = false;
          this.bouncedModel = {};
        }
      });
  }

  handleClearChqClick() {
    this.confirm(
      'Clear Cheque',
      'Are you sure you want to clear this bounced cheque transaction?'
    ).then(() => {
      this.clearChq();
    });
  }

  clearChq() {
    this.srv.loading = true;
    this.srv.clearChq(this.srv._data.id).subscribe({
      next: res => {
        this.srv.observeChanges = false;
        this.srv.editing = false;
        this.srv.loading = false;
        this.srv.setData(res);
        this.fs.disable(this.srv.form);
        this.srv.modified = false;
      },
      error: () => {
        this.srv.loading = false;
      }
    });
  }

  handlePdcClick() {
    this.confirm(
      'Post Dated Cheque',
      'Are you sure you want to mark this transaction as post dated cheque?'
    ).then(() => {
      this.srv.setPDC();
    });
  }

  handleUnmarkPdcClick() {
    this.confirm(
      'Post Dated Cheque',
      'Are you sure you want to unmark this post dated cheque?'
    ).then(() => {
      this.srv.setPDC();
    });
  }

  handleSavePdcClick() {
    this.confirm(
      'Post Dated Cheque',
      'Are you sure you want to mark this transaction as post dated cheque?'
    ).then(() => {
      this.srv.loading = true;
      this.srv.setAndSavePDC(this.srv._data.id).subscribe({
        next: res => {
          this.srv.observeChanges = false;
          this.srv.editing = false;
          this.srv.loading = false;
          this.srv.setData(res);
          this.fs.disable(this.srv.form);
          this.srv.modified = false;
        },
        error: () => {
          this.srv.loading = false;
        }
      });
    });
  }

  handleMakeRecurringTransactionClick() {
    const formControls = Object.keys(this.srv.form.controls).reduce(
      (acc, key) => {
        acc[key] = this.srv.form.controls[key].value;
        return acc;
      },
      {} as { [key: string]: any }
    );

    this.recurringTransaction.openAdd(
      this.srv.aclKey?.toUpperCase(),
      formControls
    );
  }

  recurringTransactionTemplateCallback() {
    this.router.navigateByUrl('/company/recurring-transactions');
  }

  handleSaveUnmarkPdcClick() {
    this.confirm(
      'Post Dated Cheque',
      'Are you sure you want to unmark this post dated cheque?'
    ).then(() => {
      this.srv.loading = true;
      this.srv.setAndSaveUnmarkPDC(this.srv._data.id).subscribe({
        next: res => {
          this.srv.observeChanges = false;
          this.srv.editing = false;
          this.srv.loading = false;
          this.srv.setData(res);
          this.fs.disable(this.srv.form);
          this.srv.modified = false;
        },
        error: () => {
          this.srv.loading = false;
        }
      });
    });
  }

  confirmTransferToSo() {
    this.modal.confirm({
      nzTitle: 'Transfer to Sales Order',
      nzContent:
        'Are you sure you want to transfer this document to sales order?',
      nzOnOk: () => this.transferToSo()
    });
  }

  transferToSo() {
    this.srv.transferToSo(this.srv._data.id);
  }

  confirmTransferToDo() {
    this.modal.confirm({
      nzTitle: 'Transfer to Delivery Order',
      nzContent:
        'Are you sure you want to transfer this document to delivery order?',
      nzOnOk: () => this.transferToDo()
    });
  }

  transferToDo() {
    this.srv.transferToDo(this.srv._data.id);
  }

  confirmTransferToSinv() {
    this.modal.confirm({
      nzTitle: 'Transfer to Sales Invoice',
      nzContent:
        'Are you sure you want to transfer this document to sales invoice?',
      nzOnOk: () => this.transferToSinv()
    });
  }

  confirmTransferToCs() {
    this.modal.confirm({
      nzTitle: 'Transfer to Cash Sale',
      nzContent:
        'Are you sure you want to transfer this document to cash sale?',
      nzOnOk: () => this.transferToCs()
    });
  }

  confirmTransferToCsr() {
    //Not approved doc should not be able to transfer to cash sale refund
    if (this.srv._data.documentStatus !== 2) return;

    this.modal.confirm({
      nzTitle: 'Transfer to Cash Sale Refund',
      nzContent:
        'Are you sure you want to transfer this document to cash sale refund?',
      nzOnOk: () => this.transferToCsr()
    });
  }

  transferToSinv() {
    this.srv.transferToSinv(this.srv._data.id);
  }

  transferToCs() {
    this.srv.transferToCs(this.srv._data.id);
  }

  transferToCsr() {
    this.srv.transferToCsr(this.srv._data.id);
  }

  confirmTransferToPo() {
    this.modal.confirm({
      nzTitle: 'Transfer to Purchase Order',
      nzContent:
        'Are you sure you want to transfer this document to purchase order?',
      nzOnOk: () => this.transferToPo()
    });
  }

  transferToPo() {
    this.srv.transferToPo(this.srv._data.id);
  }

  confirmTransferToGrn() {
    this.modal.confirm({
      nzTitle: 'Transfer to Goods Received Note',
      nzContent:
        'Are you sure you want to transfer this document to goods received note?',
      nzOnOk: () => this.transferToGrn()
    });
  }

  transferToGrn() {
    this.srv.transferToGrn(this.srv._data.id);
  }

  confirmTransferToPinv() {
    this.modal.confirm({
      nzTitle: 'Transfer to Purchase Invoice',
      nzContent:
        'Are you sure you want to transfer this document to purchase invoice?',
      nzOnOk: () => this.transferToPinv()
    });
  }

  transferToPinv() {
    this.srv.transferToPinv(this.srv._data.id);
  }

  confirmTransferToCp() {
    this.modal.confirm({
      nzTitle: 'Transfer to Cash Purchase',
      nzContent:
        'Are you sure you want to transfer this document to cash purchase?',
      nzOnOk: () => this.transferToCp()
    });
  }

  confirmTransferToCpf() {
    //Not approved doc should not be able to transfer to cash purchase refund
    if (this.srv._data.documentStatus !== 2) return;

    this.modal.confirm({
      nzTitle: 'Transfer to Cash Purchase Refund',
      nzContent:
        'Are you sure you want to transfer this document to cash purchase refund?',
      nzOnOk: () => this.transferToCpf()
    });
  }

  transferToCp() {
    this.srv.transferToCp(this.srv._data.id);
  }

  transferToCpf() {
    this.srv.transferToCpf(this.srv._data.id);
  }

  _transferDocType: 'QT' | 'SO' | 'DO' | 'PR' | 'PO' | 'CP' | 'CS' | 'GRN' =
    'QT';
  get transferDocType() {
    return this._transferDocType;
  }

  set transferDocType(
    val: 'QT' | 'SO' | 'DO' | 'PR' | 'PO' | 'GRN' | 'CS' | 'CP'
  ) {
    this._transferDocType = val;
  }

  get customerId() {
    return this.srv.form.controls['customerId'].value;
  }

  get supplierId() {
    return this.srv.form.controls['supplierId'].value;
  }

  _transferredDocIds: any[] = [];
  get transferredDocIds() {
    return this._transferredDocIds;
  }

  set transferredDocIds(val: any[]) {
    this._transferredDocIds = val;
  }

  get transferToDocCode() {
    if (this.id == '0' || !this.id) return null;
    return this.srv.form.controls['docCode'].value;
  }

  showQtTransferModal() {
    const customerId = this.srv.form.controls['customerId'].value;
    if (!customerId) {
      this.modal.error({
        nzTitle: 'Transfer from Quotation Failed',
        nzContent: 'Customer is required!'
      });
      return;
    }

    this.transferDocType = 'QT';
    const dtls = <any[]>this.srv.form.controls['itemDetails'].value;
    this.transferredDocIds = dtls
      .filter(o => o.transferFromQuotationDetail)
      .map(o => o.transferFromQuotationDetail.quotationId);
    this.trfTitle = 'Transfer from Quotation';
    this.trfVisible = true;
    this.trfModel = {};
    this.srv
      .onAction('doDocTransfer', this.srv.form.value)
      .then(() => {
        this.cdr.detectChanges();
      })
      .catch(() => {});
  }

  showSoTransferModal() {
    const customerId = this.srv.form.controls['customerId'].value;
    if (!customerId) {
      this.modal.error({
        nzTitle: 'Transfer from Sales Order Failed',
        nzContent: 'Customer is required!'
      });
      return;
    }

    this.transferDocType = 'SO';
    const dtls = <any[]>this.srv.form.controls['itemDetails'].value;
    this.transferredDocIds = dtls
      .filter(o => o.transferFromSalesOrderDetail)
      .map(o => o.transferFromSalesOrderDetail.salesOrderId);
    this.trfTitle = 'Transfer from Sales Order';
    this.trfVisible = true;
    this.trfModel = {};
    this.srv
      .onAction('doDocTransfer', this.srv.form.value)
      .then(() => {
        this.cdr.detectChanges();
      })
      .catch(() => {});
  }

  showDoTransferModal() {
    const customerId = this.srv.form.controls['customerId'].value;
    if (!customerId) {
      this.modal.error({
        nzTitle: 'Transfer from Delivery Order Failed',
        nzContent: 'Customer is required!'
      });
      return;
    }

    this.transferDocType = 'DO';
    const dtls = <any[]>this.srv.form.controls['itemDetails'].value;
    this.transferredDocIds = dtls
      .filter(o => o.transferFromDeliveryOrderDetail)
      .map(o => o.transferFromDeliveryOrderDetail.deliveryOrderId);
    this.trfTitle = 'Transfer from Delivery Order';
    this.trfVisible = true;
    this.trfModel = {};
    this.srv
      .onAction('doDocTransfer', this.srv.form.value)
      .then(() => {
        this.cdr.detectChanges();
      })
      .catch(() => {});
  }

  showPrTransferModal() {
    const supplierId = this.srv.form.controls['supplierId'].value;
    if (!supplierId) {
      this.modal.error({
        nzTitle: 'Transfer from Purchase Requisition Failed',
        nzContent: 'Supplier is required!'
      });
      return;
    }

    this.transferDocType = 'PR';
    const dtls = <any[]>this.srv.form.controls['itemDetails'].value;
    this.transferredDocIds = dtls
      .filter(o => o.transferFromPurchaseRequisitionDetail)
      .map(o => o.transferFromPurchaseRequisitionDetail.purchaseRequisitionId);
    this.trfTitle = 'Transfer from Purchase Requisition';
    this.trfVisible = true;
    this.trfModel = {};
    this.srv
      .onAction('doDocTransfer', this.srv.form.value)
      .then(() => {
        this.cdr.detectChanges();
      })
      .catch(() => {});
  }

  showPoTransferModal() {
    const supplierId = this.srv.form.controls['supplierId'].value;
    if (!supplierId) {
      this.modal.error({
        nzTitle: 'Transfer from Purchase Order Failed',
        nzContent: 'Supplier is required!'
      });
      return;
    }

    this.transferDocType = 'PO';
    const dtls = <any[]>this.srv.form.controls['itemDetails'].value;
    this.transferredDocIds = dtls
      .filter(o => o.transferFromPurchaseOrderDetail)
      .map(o => o.transferFromPurchaseOrderDetail.purchaseOrderId);
    this.trfTitle = 'Transfer from Purchase Order';
    this.trfVisible = true;
    this.trfModel = {};
    this.srv
      .onAction('doDocTransfer', this.srv.form.value)
      .then(() => {
        this.cdr.detectChanges();
      })
      .catch(() => {});
  }

  showGrnTransferModal() {
    const supplierId = this.srv.form.controls['supplierId'].value;
    if (!supplierId) {
      this.modal.error({
        nzTitle: 'Transfer from Goods Received Note Failed',
        nzContent: 'Supplier is required!'
      });
      return;
    }

    this.transferDocType = 'GRN';
    const dtls = <any[]>this.srv.form.controls['itemDetails'].value;
    this.transferredDocIds = dtls
      .filter(o => o.transferFromGoodsReceivedNoteDetail)
      .map(o => o.transferFromGoodsReceivedNoteDetail.goodsReceivedNoteId);
    this.trfTitle = 'Transfer from Goods Received Note';
    this.trfVisible = true;
    this.trfModel = {};
    this.srv
      .onAction('doDocTransfer', this.srv.form.value)
      .then(() => {
        this.cdr.detectChanges();
      })
      .catch(() => {});
  }

  showCSTransferModal() {
    let customerId = this.srv.form.controls['customerId'].value;

    this.transferDocType = 'CS';
    const dtls = <any[]>this.srv.form.controls['itemDetails'].value;
    this.transferredDocIds = dtls
      .filter(o => o.transferFromCashSaleDetail)
      .map(o => o.transferFromCashSaleDetail.cashSaleId);
    this.trfTitle = 'Transfer from Cash Sale';
    this.trfVisible = true;
    this.trfModel = {};
    this.srv
      .onAction('doDocTransfer', this.srv.form.value)
      .then(() => {
        this.cdr.detectChanges();
      })
      .catch(() => {});
  }

  showCPTransferModal() {
    const supplierId = this.srv.form.controls['supplierId'].value;

    this.transferDocType = 'CP';
    const dtls = <any[]>this.srv.form.controls['itemDetails'].value;
    this.transferredDocIds = dtls
      .filter(o => o.transferFromCashPurchaseDetail)
      .map(o => o.transferFromCashPurchaseDetail.cashPurchaseId);
    this.trfTitle = 'Transfer from Cash Purchase';
    this.trfVisible = true;
    this.trfModel = {};
    this.srv
      .onAction('doDocTransfer', this.srv.form.value)
      .then(() => {
        this.cdr.detectChanges();
      })
      .catch(() => {});
  }

  doDocTransfer() {
    this.processingType = 'transfer';
    this.srv
      .doDocTransfer(this.transferDocType)
      .pipe(
        finalize(() => {
          this.processingType = '';
          this.srv.observeChanges = true;
          this.srv.modified = true;
        })
      )
      .subscribe({
        next: res => {
          this.srv.observeChanges = false;
          this.srv.setData(res);
          this.srv.modified = false;
          this.trfVisible = false;
          this.trfModel = {};
        }
      });
  }

  cancel() {
    const modified = this.srv.modified; // !compareValue(this.dataSrv.form?.value, this.dataSrv.data);
    if (modified) {
      const modelRef = this.modal.confirm({
        nzTitle: 'Discard Changes?',
        nzContent: 'Changes you made may not be saved.',
        nzOnOk: () => {
          modelRef.close();
          this.clearPathFromQueryStore();
          this.srv.modified = false;
          if (this.id === '0' && this.isConvertDoc) {
            this.closeReuseTab();
          } else if (this.id === '0') {
            this.closeReuseTab();
          } else {
            this.resetChanges();
          }
        },
        nzOkText: 'Discard Changes',
        nzCancelText: 'Return'
      });
    } else {
      if (this.id === '0' && this.isConvertDoc) {
        this.closeReuseTab();
      } else if (this.id === '0') {
        this.closeReuseTab();
      } else {
        this.resetChanges();
      }
    }
  }

  private resetChanges() {
    this.clearPathFromQueryStore();
    this.srv.observeChanges = false;
    if (this.srv.form) {
      this.srv.form.patchValue(this.srv._data);
      this.srv.resetDataToOriginal();
      this.fs.disable(this.srv.form);
      this.srv.editing = false;
      this.srv.modified = false;
    }
  }

  reload() {
    this.clearPathFromQueryStore();
    this.srv.loading = true;
    this.loadingSrv.open();
    this.srv
      .get(this.id)
      .pipe(
        finalize(() => {
          this.loadingSrv.close();
          this.srv.loading = false;
        })
      )
      .subscribe({
        next: res => {
          this.srv.setData(res);
          this.updateTitle();
          this.toggleMode('view');
        },
        error: () => {
          this.srv.loading = false;
        }
      });
  }

  confirm(title: string, content: string) {
    return new Promise((resolve, reject) => {
      this.modal.confirm({
        nzTitle: title,
        nzContent: content,
        nzOnOk: () => {
          resolve(true);
        }
      });
    });
  }

  async handleDeleteClick() {
    if (this.dimmedDelete) return;

    const body = this.buildSaveBody();
    if (this.sstReturnCheck) {
      const sstPassed = await this.performFullSSTCheck(body);
      if (!sstPassed) {
        this.saving = false;
        this.srv.observeChanges = true;
        this.cdr.detectChanges();
        return;
      }
    }
    this.modal.confirm({
      nzTitle: 'Confirmation',
      nzContent: 'Are you sure you want to delete this record?',
      nzClassName: 'modal-btn',
      nzOnOk: () => {
        this.delete();
      },
      nzOkDanger: true,
      nzOkText: 'Confirm Delete'
    });
  }

  delete() {
    this.clearPathFromQueryStore();
    this.srv.deleting = true;
    this.srv.loading = true;
    this.srv
      .delete(this.srv._data.id)
      .then(() => {
        this.listSrv.removeRow(this.srv._data);
        this.srv.deleting = false;
        this.srv.loading = false;
        this.srv.modified = false;
        this.close();
      })
      .catch(() => {
        this.srv.deleting = false;
        this.srv.loading = false;
        this.reload();
      });
  }

  close() {
    this.clearPathFromQueryStore();
    const currUrl = this.router.url;
    this.router.navigate(['../../'], { relativeTo: this.route }).then(() => {
      this.reuseTabSrv.close(currUrl);
    });
  }

  closeReuseTab() {
    this.clearPathFromQueryStore();
    const currUrl = this.reuseTabSrv.curUrl;
    this.router.navigate(['../../'], { relativeTo: this.route }).then(() => {
      this.reuseTabSrv.close(currUrl);
    });
  }

  private updateTitle() {
    let title = `${this.srv.tabPrefix} - New`;
    if (this.srv.code) {
      title = this.srv.code;
    }
    const cachedTab = this.reuseTabSrv.get(
      this.reuseTabSrv.getUrl(this.route.snapshot)
    );
    if (cachedTab) {
      cachedTab.title.text = title;
    }
    this.reuseTabSrv.title = title;
  }

  showCreditLimitModal(
    docDate: any,
    currency: any,
    current: any,
    transactionType: any,
    customerId: any,
    alreadySavedAmount: any
  ): Promise<boolean> {
    return new Promise(resolve => {
      const today = docDate ? new Date(docDate) : new Date();
      forkJoin({
        outstanding: this.srv.http.get(
          `api/CreditLimitSettings/GetOutstanding?docType=${transactionType}&customerId=${customerId}`
        ),
        creditLimit: this.srv.http.get(
          `api/CreditLimitSettings/GetCreditLimit?customerId=${customerId}`
        )
      }).subscribe({
        next: ({ outstanding, creditLimit }) => {
          if (
            !creditLimit ||
            !creditLimit.isActive ||
            (creditLimit.validUntil &&
              today > new Date(creditLimit.validUntil)) ||
            (creditLimit.applyForTransactions != '' &&
              !creditLimit.applyForTransactions.includes(transactionType))
          ) {
            resolve(true);
            return;
          }

          var finalOutstanding =
            outstanding - alreadySavedAmount > 0
              ? outstanding - alreadySavedAmount
              : 0;
          var exceededAmount =
            finalOutstanding + current - creditLimit.creditLimit;
          if (exceededAmount <= 0) {
            resolve(true);
            return;
          }

          const modalRef = this.modal.create({
            nzTitle: 'Credit Limit Exceeded',
            nzContent: GlobalCreditLimitModalComponent,
            nzData: {
              currency: currency,
              current: current,
              outstanding: finalOutstanding,
              exceededAmount: exceededAmount,
              alreadySavedAmount: alreadySavedAmount,
              globalCreditLimit: creditLimit
            },
            nzFooter: null // Footer handled inside the component
          });

          modalRef.afterClose.subscribe(result => {
            resolve(result); // Result should be true (OK) or false (Cancel)
          });
        },
        error: error => {
          console.error('Error fetching data:', error);
        }
      });
    });
  }

  private getChangedKnockoffItems(current: any[], original: any[]): any[] {
    const currentMap = new Map(current.map(i => [i.docId, i]));
    const originalMap = new Map(original.map(i => [i.docId, i]));
    const changed: any[] = [];

    for (const item of current) {
      if (item.paymentAmount > 0) {
        const orig = originalMap.get(item.docId);
        if (!orig || orig.paymentAmount === 0) changed.push(item);
      }
    }
    for (const item of original) {
      if (item.paymentAmount > 0) {
        const curr = currentMap.get(item.docId);
        if (!curr || curr.paymentAmount === 0) changed.push(item);
      }
    }
    return changed;
  }

  validateKnockoffTaxDate(
    documentTaxDate: any,
    knockoffItems: any[]
  ): Promise<any> {
    const requestBody = {
      documentTaxDate: documentTaxDate,
      knockoffItems: knockoffItems.map((item: any) => ({
        taxDate: item.taxDate,
        hasSvt6: item.hasSvt6 || false,
        hasSvt8: item.hasSvt8 || false
      }))
    };
    return this.srv.http
      .post<{
        isValid: boolean;
        status: boolean;
      }>('api/sstreturns/ValidateKnockoffTaxDate?_silent=true', requestBody)
      .toPromise();
  }

  async performKnockoffSSTCheck(body: any): Promise<boolean> {
    const currentKnockoff = body.knockoff || [];
    const originalKnockoff = this.srv._data?.knockoff || [];
    const changedItems = this.getChangedKnockoffItems(
      currentKnockoff,
      originalKnockoff
    );
    if (changedItems.length === 0) return true;

    try {
      const response = await this.validateKnockoffTaxDate(
        body.taxDate,
        changedItems
      );
      if (!response.isValid) {
        await new Promise<void>(resolve => {
          this.modal.warning({
            nzTitle: 'SST Return Submission',
            nzContent:
              'The SST Return for the affected tax period has already been submitted. ' +
              'Matching or unmatching transactions that affect the submitted SST Return period is not allowed.',
            nzOnOk: () => resolve()
          });
        });
        return false;
      }
    } catch (error) {
      console.error('Error validating knockoff tax date:', error);
    }
    return true;
  }

  async performFullSSTCheck(body: any): Promise<boolean> {
    const knockoffPassed = await this.performKnockoffSSTCheck(body);
    if (!knockoffPassed) return false;

    let isValidTaxDate = false;
    while (!isValidTaxDate) {
      let details =
        body.details?.length > 0 ? body.details : body.itemDetails || [];

      if (!details || details.length === 0) {
        if (body.docTaxCode) {
          details = [
            { taxCode: body.docTaxCode, taxCodeId: body.docTaxCodeId }
          ];
        }
      }

      let response: any;
      try {
        response = await this.validateTaxDate(body.taxDate, [...details]);
        if (response.isValid) {
          isValidTaxDate = true;
        } else {
          body.taxDate = await this.changeTaxDateModal(
            body.docDate,
            body.taxDate,
            [...details],
            response?.status
          );
          if (!body.taxDate) break;
        }
      } catch (error) {
        console.error('Error validating tax date:', error);
        body.taxDate = await this.changeTaxDateModal(
          body.docDate,
          body.taxDate,
          [...details],
          response?.status
        );
        if (!body.taxDate) break;
      }
    }
    return isValidTaxDate;
  }

  validateTaxDate(taxDate?: Date, itemDetails?: any[]): Promise<any> {
    // Filter out any null values from itemDetails array and items with null TaxCode
    const filteredItems = itemDetails
      ? itemDetails.filter(
          item => item !== null && item !== undefined && item.taxCode !== null
        )
      : [];
    const requestBody = { taxDate: taxDate, itemDetails: filteredItems };
    return this.srv.http
      .post<{
        isValid: boolean;
        status: boolean;
      }>('api/sstreturns/ValidateTaxDate?_silent=true', requestBody)
      .toPromise();
  }

  changeTaxDateModal(
    docDate?: Date,
    taxDate?: Date,
    itemDetails?: any,
    sstReturnStatus?: boolean
  ): Promise<Date | null> {
    return new Promise(resolve => {
      const modal: NzModalRef<TaxDatePickerComponent> =
        this.modal.create<TaxDatePickerComponent>({
          nzTitle: 'Set Tax Date',
          nzContent: TaxDatePickerComponent,
          nzFooter: [
            { label: 'Cancel', onClick: () => modal.close(null) },
            {
              label: 'OK',
              type: 'primary',
              onClick: async (componentInstance: TaxDatePickerComponent) => {
                const response = await componentInstance.validateTaxDate();

                if (response) {
                  modal.close(componentInstance.taxDate);
                }

                return response;
              }
            }
          ]
        });

      // Get the component instance and set properties directly
      const componentInstance = modal.getContentComponent();
      componentInstance.itemDetails = itemDetails;
      componentInstance.documentDate = docDate || new Date();
      componentInstance.taxDate = taxDate || new Date();
      componentInstance.sstReturnStatus = sstReturnStatus ?? false; // Set the initial value

      modal.afterClose.subscribe(result => {
        resolve(result);
      });
    });
  }

  //for save and send
  onModalClosed(sendOption: string) {
    this.clearPathFromQueryStore();
    if (this.pendingCloseUrl) {
      this.reuseTabSrv.close(this.pendingCloseUrl);
      this.pendingCloseUrl = null;
      console.log(`Closed old tab after ${sendOption} modal finished`);
    }
  }

  MarkAsClosed() {
    this.confirm(
      'Mark As Closed',
      'Are you sure you want to mark this as closed?'
    ).then(() => {
      this.srv.loading = true;
      (this.srv.close(this.srv._data.id) as unknown as Promise<any>)
        .then(() => {
          this.srv.loading = false;
          this.reload();
        })
        .catch((error: any) => {
          this.srv.loading = false;
          // Show error message to user
          console.error('Failed to close document:', error);
          // You might want to show a toast notification or alert here
        });
    });
  }

  markAsClosedIsVisible() {
    // Mark as Closed is visible for specific document types in their detail views
    const allowedAclKeys = ['qt', 'so', 'do', 'pr', 'po', 'grn'];
    return (
      allowedAclKeys.includes(this.srv.aclKey) &&
      !this.srv._data.isCancelled &&
      !this.srv._data.isClosed
    );
  }

  Reopen() {
    this.confirm(
      'Closed Transaction',
      'Are you sure you want to reopen this transaction?'
    ).then(() => {
      this.srv.loading = true;
      (this.srv.reOpen(this.srv._data.id) as unknown as any).subscribe({
        next: () => {
          this.srv.loading = false;
          this.reload();
        },
        error: (error: any) => {
          this.srv.loading = false;
          // Show error message to user
          console.error('Failed to reopen document:', error);
          // You might want to show a toast notification or alert here
        }
      });
    });
  }

  reOpenIsVisible() {
    const allowedAclKeys = ['qt', 'so', 'do', 'pr', 'po', 'grn'];
    return allowedAclKeys.includes(this.srv.aclKey) && this.srv._data.isClosed;
  }
}
